import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, Mail, AtSign, Building2, Users, CreditCard, 
  Trash2, AlertTriangle, ArrowLeft, Save, Loader2, Camera, Upload, Link as LinkIcon, Check
} from 'lucide-react';

const PLAN_NAMES = {
  brand_growth: "Brand Growth",
  brand_starter: "Brand Starter",
  brand_enterprise: "Brand Enterprise",
  influencer_starter: "Influencer Starter",
  influencer_pro: "Influencer Pro",
  personal: "Personal"
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, token, logout, refreshUser, loading: authLoading } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState('');
  const [profileImage, setProfileImage] = useState(user?.profile_image || null);
  
  // Check if user is on Pro/Enterprise plan or Personal plan (no upgrade needed)
  const isProPlan = user?.plan_id === 'brand_enterprise' || user?.plan_id === 'influencer_pro';
  const isPersonalPlan = user?.plan_id === 'personal';
  const shouldShowUpgrade = !isProPlan && !isPersonalPlan;
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    website: user?.website || ''
  });
  
  // Store link state for influencers (synced with My Links tab)
  const [affiliateLink, setAffiliateLink] = useState(user?.store_link || '');
  const [savingAffiliateLink, setSavingAffiliateLink] = useState(false);
  const [affiliateLinkSaved, setAffiliateLinkSaved] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Load store link when user data changes
  useEffect(() => {
    if (user?.user_type === 'influencer') {
      setAffiliateLink(user.store_link ?? '');
    }
  }, [user]);
  
  // Save store link function (syncs with My Links tab)
  const saveAffiliateLink = async () => {
    if (!token) return;
    
    setSavingAffiliateLink(true);
    setAffiliateLinkSaved(false);
    setMessage('');
    
    // Auto-prepend https:// if bare URL
    let normalizedLink = affiliateLink.trim();
    if (normalizedLink && !normalizedLink.startsWith('http://') && !normalizedLink.startsWith('https://')) {
      normalizedLink = `https://${normalizedLink}`;
      setAffiliateLink(normalizedLink);
    }
    
    try {
      const res = await fetch(`${API_URL}/api/influencer/links`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ store_link: normalizedLink, instagram_link: user?.instagram_link ?? '' })
      });
      
      if (res.ok) {
        setAffiliateLinkSaved(true);
        setMessage('Affiliate link saved successfully!');
        if (refreshUser) refreshUser();
        setTimeout(() => setAffiliateLinkSaved(false), 3000);
      } else {
        setMessage('Failed to save affiliate link');
      }
    } catch (err) {
      console.error('Error saving affiliate link:', err);
      setMessage('Error saving affiliate link');
    } finally {
      setSavingAffiliateLink(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage('Image must be less than 2MB');
      return;
    }

    setUploadingImage(true);
    setMessage('');

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setProfileImage(base64);

      try {
        const res = await fetch(`${API_URL}/api/user/profile-image`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ profile_image: base64 })
        });

        if (res.ok) {
          await refreshUser();
          setMessage('Profile image updated successfully!');
        } else {
          setMessage('Failed to upload image');
        }
      } catch (err) {
        setMessage('Error uploading image');
      } finally {
        setUploadingImage(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const res = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        await refreshUser();
        setMessage('Profile updated successfully!');
      } else {
        setMessage('Failed to update profile');
      }
    } catch (err) {
      setMessage('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== 'DELETE') return;
    
    setDeleting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/user/account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        logout();
        navigate('/');
      } else {
        setMessage('Failed to delete account');
      }
    } catch (err) {
      setMessage('Error deleting account');
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const TypeIcon = user.user_type === 'brand' ? Building2 : 
                   user.user_type === 'influencer' ? Users : User;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Link>
          <Link to="/" className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
            VirtualFit
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-8">Account Settings</h1>

        {message && (
          <div className={`mb-6 p-4 rounded-xl ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message}
          </div>
        )}

        {/* Profile Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">Profile Information</h2>
          
          {/* Profile Image Upload - Only for Brands and Influencers */}
          {(user.user_type === 'brand' || user.user_type === 'influencer') && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {user.user_type === 'brand' ? 'Brand Logo' : 'Profile Picture'}
              </label>
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <div className={`w-24 h-24 rounded-xl overflow-hidden border-2 border-dashed ${profileImage ? 'border-transparent' : 'border-gray-300'} flex items-center justify-center bg-gray-50`}>
                    {profileImage ? (
                      <img 
                        src={profileImage.startsWith('data:') ? profileImage : `data:image/png;base64,${profileImage}`}
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <TypeIcon className="w-10 h-10 text-gray-300" />
                    )}
                  </div>
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-lg hover:shadow-md transition-all">
                    <Camera className="w-4 h-4 mr-2" />
                    {profileImage ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG up to 2MB</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <div className="flex items-center">
                <User className="w-5 h-5 text-gray-400 mr-3" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-gray-400 mr-3" />
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            {/* Handle, Bio, Website - Only for Brand and Influencer */}
            {user.user_type !== 'personal' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Handle</label>
                  <div className="flex items-center">
                    <AtSign className="w-5 h-5 text-gray-400 mr-3" />
                    <input
                      type="text"
                      value={user.handle}
                      disabled
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                      data-testid="settings-handle-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Your profile: <Link to={user.user_type === 'influencer' ? `/influencer/${user.handle}` : `/profile/${user.handle}`} className="text-rose-500 hover:underline" data-testid="settings-view-profile-link">virtualfit.work/@{user.handle}</Link>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                    placeholder="Tell us about yourself..."
                    data-testid="settings-bio-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                    placeholder="https://yourwebsite.com"
                    data-testid="settings-website-input"
                  />
                </div>
              </>
            )}

            {/* Affiliate Link - Only for Influencers */}
            {user.user_type === 'influencer' && (
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <LinkIcon className="w-4 h-4 mr-2 text-purple-500" />
                    Affiliate Link
                  </div>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  This link will appear on your public profile. Direct followers to your store or affiliate links.
                </p>
                <input
                  type="url"
                  value={affiliateLink}
                  onChange={(e) => setAffiliateLink(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                  placeholder="https://amazon.com/shop/yourstore or your affiliate link"
                  data-testid="settings-affiliate-link-input"
                />
                <div className="flex items-center gap-3 mt-3">
                  <button 
                    onClick={saveAffiliateLink}
                    disabled={savingAffiliateLink}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                    data-testid="settings-save-affiliate-btn"
                  >
                    {savingAffiliateLink ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : affiliateLinkSaved ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {affiliateLinkSaved ? 'Saved!' : 'Save Affiliate Link'}
                  </button>
                  {affiliateLink && (
                    <Link 
                      to={`/influencer/${user.handle}`}
                      className="text-sm text-purple-500 hover:text-purple-600"
                      data-testid="settings-view-on-profile-link"
                    >
                      View on profile →
                    </Link>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-6 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              Save Changes
            </button>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">Subscription</h2>
          
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-xl ${
              user.user_type === 'brand' ? 'bg-blue-100' :
              user.user_type === 'influencer' ? 'bg-purple-100' : 'bg-rose-100'
            }`}>
              <TypeIcon className={`w-6 h-6 ${
                user.user_type === 'brand' ? 'text-blue-500' :
                user.user_type === 'influencer' ? 'text-purple-500' : 'text-rose-500'
              }`} />
            </div>
            
            <div className="flex-1">
              <p className="font-semibold text-gray-800">{PLAN_NAMES[user.plan_id] || user.plan_id}</p>
              <p className="text-sm text-gray-500 capitalize">{user.user_type} Account</p>
              
              <div className="mt-4 flex items-center">
                <CreditCard className="w-5 h-5 text-gray-400 mr-2" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user.subscription_status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {user.subscription_status === 'active' ? 'Active' : 'Pending Payment'}
                </span>
              </div>
              
              <div className="mt-2 text-sm text-gray-600">
                Images used: <strong>{user.images_used || 0}</strong> / {user.images_limit || 0} this month
              </div>
              
              {/* Subscription Management Links */}
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/subscription"
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Subscription
                </Link>
                
                {shouldShowUpgrade && (
                  <Link
                    to="/subscription"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:shadow-md transition-all"
                  >
                    Upgrade Plan
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border-2 border-red-100">
          <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Danger Zone
          </h2>
          
          <p className="text-gray-600 mb-4">
            Once you delete your account, there is no going back. This will permanently delete your profile, 
            {user.user_type === 'brand' && ' portfolio,'} and all associated data.
          </p>
          
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Delete Account?</h3>
            <p className="text-gray-600 mb-4">
              This action cannot be undone. All your data will be permanently deleted including:
            </p>
            <ul className="text-sm text-gray-600 mb-4 space-y-1">
              <li>• Your profile and public page (@{user.handle})</li>
              {user.user_type === 'brand' && <li>• Your clothing portfolio</li>}
              {user.user_type === 'influencer' && <li>• Your featured looks and affiliate data</li>}
              <li>• Your subscription and payment history</li>
            </ul>
            
            <p className="text-sm font-medium text-gray-800 mb-2">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg mb-4"
              placeholder="DELETE"
            />
            
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteConfirm !== 'DELETE' || deleting}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
